import { Component, OnDestroy, OnInit, input, output, inject, DOCUMENT } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApContentService } from '@ap/content';
import { ModalClosedReason, ModalClosedResult, ModalContainer, ModalOptions, ApModalService } from '@ap/modal';

import { KeepAliveModalClosedReason } from './keep-alive.model';
import { ApKeepAliveService } from './keep-alive.service';
import { ApKeepAliveModalComponent } from './modal/keep-alive-modal.component';
import content from './modal/keep-alive-modal.content.json';

/**
 * @ngdoc component
 * @name ApKeepAliveComponent
 */
@Component({
    selector: 'ap-keep-alive',
    template: ''
})
export class ApKeepAliveComponent implements OnInit, OnDestroy {
    private readonly keepAliveService = inject(ApKeepAliveService);
    private readonly modalService = inject(ApModalService);
    private readonly contentService = inject(ApContentService);
    private readonly document = inject(DOCUMENT);

    /**
     * Time until the keep alive modal is shown.
     * Defaults to 8 minutes
     */
    public readonly timeToThreshold = input<number>(8 * 60 * 1000);

    /**
     * Time until keep alive ends / session is ended.
     * The timeToEnd should be always minimum 20 seconds for A11Y purposes.
     */
    public readonly timeToEnd = input(20000, {
        transform: (value: number): number => {
            const greaterValue = value;
            if (!value || value <= 20000) {
                return 20000;
            }
            return greaterValue;
        }
    });

    /**
     * Emits event with `KeepAliveModalClosedReason` when keep alive modal is closed.
     */
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    public readonly onKeepAliveModalClosed = output<KeepAliveModalClosedReason>();

    /**
     * Emits event every time heartbeat happens.
     */
    public readonly heartbeat = output<boolean>();

    private readonly HEARTBEAT_PERIOD: number = 9 * 60 * 1000; // 9 minutes

    private modalContainer: ModalContainer;
    private modalIsOpen = false;
    private timeLeft: number;
    private readonly destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this.keepAliveService.startTimer(this.timeToThreshold(), this.timeToEnd());
        this.contentService.addContentItems(content);
        this.keepAliveService.timeLeft$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (timeLeft) => {
                this.timeLeft = timeLeft;
            }
        });

        this.keepAliveService.heartbeatTimePassed$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (heartbeatTimePassed) => {
                if (this.shouldGiveHeartbeat(heartbeatTimePassed)) {
                    this.keepAliveService.resetHeartbeatTimer();
                    this.heartbeat.emit(true);
                }
            }
        });

        this.keepAliveService.thresholdIsReached$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (thresholdReached) => {
                if (thresholdReached) {
                    this.openModal();
                }
            }
        });

        this.keepAliveService.timeToEndIsReached$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (timeToEndReached) => {
                if (timeToEndReached) {
                    if (this.modalIsOpen) {
                        this.modalContainer.close(ModalClosedReason.DECLINED);
                    } else {
                        this.endKeepAlive(KeepAliveModalClosedReason.EXPIRED);
                    }
                    // If the modal is closed, we call endKeepAlive directly.
                    // If the modal is opened, we close the modal with ModalClosedReason.DECLINED.
                    // Closing the modal with ModalClosedReason.DECLINED will trigger endKeepAlive.
                    //
                    // If the modal is open we could call endKeepAlive instead of closing the modal.
                    // However that might result in double unsubscribe's if the user closes the modal at the same time.
                }
            }
        });

        fromEvent(this.document, 'input')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onActivity());
        fromEvent(this.document, 'mousedown')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onActivity());
        fromEvent(this.document, 'scroll')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onActivity());
        fromEvent(this.document, 'mousemove')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onActivity());
        fromEvent(this.document, 'keypress')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onActivity());
    }

    protected onActivity(): void {
        if (!this.modalIsOpen) {
            this.keepAliveService.resetTimer();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private shouldGiveHeartbeat(heartbeatTimePassed: number): boolean {
        return heartbeatTimePassed >= this.HEARTBEAT_PERIOD;
    }

    private openModal(): void {
        this.modalIsOpen = true;
        const modalOptions: ModalOptions<any> = new ModalOptions();
        modalOptions.cancelable = false;
        modalOptions.fullscreen = true;
        modalOptions.showHeader = false;
        modalOptions.ariaLabel = this.contentService.get('keepAliveModal.title');
        modalOptions.ariaDescribedby = 'keepAliveModalDescription';
        this.modalContainer = this.modalService.open(ApKeepAliveModalComponent, modalOptions);
        this.modalContainer.afterClosed().subscribe((result: ModalClosedResult) => {
            this.modalIsOpen = false;
            if (result.reason === ModalClosedReason.ACCEPTED) {
                this.keepAliveService.resetTimer();
                this.onKeepAliveModalClosed.emit(KeepAliveModalClosedReason.ACCEPTED);
            } else if (result.reason === ModalClosedReason.DECLINED) {
                if (this.timeLeft <= 0) {
                    this.endKeepAlive(KeepAliveModalClosedReason.EXPIRED);
                } else {
                    this.endKeepAlive(KeepAliveModalClosedReason.DECLINED);
                }
            }
        });
    }

    private endKeepAlive(reason: KeepAliveModalClosedReason): void {
        this.ngOnDestroy();
        this.onKeepAliveModalClosed.emit(reason);
    }
}
